/**
 * Auto-Scroll fuer das Lead Sheet.
 *
 * Bewegt das Scroll-Element mit requestAnimationFrame um eine feste
 * Pixelzahl pro Sekunde, zeitbasiert statt pro Frame.
 *
 * WARUM ES NICHT RUCKELT
 *   scrollTop landet im Browser nur auf ganzen (Geraete-)Pixeln. Bei
 *   langsamen Geschwindigkeiten faellt pro Frame weit weniger als ein Pixel
 *   an – dann springt die Seite alle paar Frames um einen Pixel, und genau
 *   das sieht man als Ruckeln. Deshalb wird die Position geteilt: der ganze
 *   Teil geht in scrollTop, der Rest (0..1 px) als translate3d auf den
 *   Inhalt. Das verschiebt die Grafikkarte stufenlos.
 *
 *   Dazu laeuft die Bewegung weich an und aus (RAMP_MS), statt schlagartig
 *   mit voller Geschwindigkeit zu starten oder stehen zu bleiben.
 */

export interface AutoScroller {
  readonly running: boolean;
  readonly speed: number;
  start(): void;
  stop(): void;
  toggle(): boolean;
  /** Geschwindigkeit in px/s; wirkt sofort, auch waehrend des Laufens. */
  setSpeed(value: number): void;
  destroy(): void;
}

export const MIN_SPEED = 2;
export const MAX_SPEED = 180;

/** Dauer des sanften Anlaufens und Auslaufens. */
const RAMP_MS = 450;

export function clampSpeed(value: number): number {
  if (!Number.isFinite(value)) return MIN_SPEED;
  return Math.min(MAX_SPEED, Math.max(MIN_SPEED, Math.round(value * 10) / 10));
}

/** Weiche Kurve 0..1 fuer An- und Auslauf. */
function ease(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
}

export function createAutoScroller(
  el: HTMLElement,
  initialSpeed: number,
  onStateChange?: (running: boolean) => void,
): AutoScroller {
  let speed = clampSpeed(initialSpeed);
  let running = false;
  /** Laeuft noch aus, obwohl schon gestoppt wurde. */
  let coasting = false;
  let frame = 0;
  let last = 0;
  let position = 0;
  let rampFrom = 0;
  let rampStart = 0;
  let rampTo = 1;
  let factor = 0;

  const content = (): HTMLElement | null => el.firstElementChild as HTMLElement | null;

  function paint(): void {
    const whole = Math.floor(position);
    if (el.scrollTop !== whole) el.scrollTop = whole;
    const rest = position - whole;
    const inner = content();
    if (inner) inner.style.transform = rest > 0.01 ? `translate3d(0, ${-rest}px, 0)` : '';
  }

  function clearOffset(): void {
    const inner = content();
    if (inner) inner.style.transform = '';
  }

  const step = (now: number) => {
    if (!running && !coasting) return;
    const dt = Math.min(0.1, (now - last) / 1000); // Sprung nach Pause begrenzen
    last = now;
    factor = rampFrom + (rampTo - rampFrom) * ease((now - rampStart) / RAMP_MS);
    position += speed * factor * dt;

    const max = el.scrollHeight - el.clientHeight;
    if (position >= max) {
      position = max;
      paint();
      clearOffset();
      coasting = false;
      stop();
      return;
    }
    paint();
    if (coasting && factor <= 0.001) {
      coasting = false;
      clearOffset();
      return;
    }
    frame = requestAnimationFrame(step);
  };

  const start = () => {
    if (running) return;
    const wasCoasting = coasting;
    running = true;
    coasting = false;
    // Von der aktuellen Position weiterlaufen, auch wenn von Hand gescrollt
    // wurde – ausser es laeuft gerade noch aus, dann nahtlos weiter.
    if (!wasCoasting) position = el.scrollTop;
    rampFrom = wasCoasting ? factor : 0;
    rampTo = 1;
    rampStart = performance.now();
    last = rampStart;
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(step);
    onStateChange?.(true);
  };

  const stop = () => {
    if (!running) return;
    running = false;
    // Weich auslaufen lassen statt schlagartig anzuhalten.
    coasting = factor > 0.01;
    rampFrom = factor;
    rampTo = 0;
    rampStart = performance.now();
    if (!coasting) {
      cancelAnimationFrame(frame);
      clearOffset();
    }
    onStateChange?.(false);
  };

  return {
    get running() {
      return running;
    },
    get speed() {
      return speed;
    },
    start,
    stop,
    toggle() {
      if (running) stop();
      else start();
      return running;
    },
    setSpeed(value: number) {
      speed = clampSpeed(value);
    },
    destroy() {
      running = false;
      coasting = false;
      cancelAnimationFrame(frame);
      clearOffset();
    },
  };
}

/**
 * Schaetzt eine Startgeschwindigkeit aus Tempo und Sheet-Laenge.
 *
 * Annahme: ein Akkord im Sheet entspricht ungefaehr einem Takt. Fuer
 * Jazz-Lead-Sheets kommt das meist grob hin; feinjustiert wird waehrend
 * des Spielens am Schieberegler.
 *
 * @param bpm Tempo in Schlaegen pro Minute
 * @param beatsPerBar Schlaege pro Takt (aus der Taktart)
 * @param bars geschaetzte Taktzahl
 * @param scrollDistance zu scrollende Strecke in Pixeln
 */
export function estimateSpeed(
  bpm: number,
  beatsPerBar: number,
  bars: number,
  scrollDistance: number,
): number | null {
  if (!bpm || bpm <= 0 || bars <= 0 || scrollDistance <= 0) return null;
  const seconds = (bars * beatsPerBar * 60) / bpm;
  if (seconds <= 0) return null;
  return clampSpeed(scrollDistance / seconds);
}

/** Liest die Schlaege pro Takt aus einer Taktart wie "4/4" oder "6/8". */
export function beatsPerBar(timeSignature: string): number {
  const match = /^\s*(\d+)\s*\/\s*(\d+)\s*$/.exec(timeSignature);
  if (!match) return 4;
  const beats = Number(match[1]);
  return beats > 0 ? beats : 4;
}
